// Handle target selection for deployed units
const targetDeploy = (
  targetZone, 
  targetIndex, 
  targetUnitId,
  yourTurn,
  gameId,
  ws,
  setError,
  selectedDeployOrderId,
  selectedDeployInstanceId,
  selectedDeployZone,
  setDeployTargetMode,
  setSelectedDeployOrderId,
  setSelectedDeployInstanceId,
  setSelectedDeployZone,
  setDeployTargetInfo,
  gameState = null,
  playerId = null
) => {
  // ✅ FIX: Allow targetDeploy during opponent's turn if it's for counter strike deploy targeting
  // Counter strike units can be targeted even during opponent's turn
  // Check if this is counter strike targeting by checking if it's opponent's turn and deployTargetMode is active
  // We'll let the backend validate this more strictly
  const isCounterStrikeTargeting = !yourTurn && selectedDeployOrderId;
  
  if ((!yourTurn && !isCounterStrikeTargeting) || !gameId || !ws || ws.readyState !== 1) {
    if (!yourTurn && !isCounterStrikeTargeting) {
      setError("Not your turn!");
      return;
    }
    setError("Connection lost. Please refresh the page.");
    return;
  }

  // ✅ VALIDATE TARGET BEFORE SENDING ACTION
  // Check if targeting friendly units is allowed for this deploy unit
  if (targetUnitId === 'friendly') {
    // For now, we'll let the server handle validation and show error if invalid
    // This prevents targeting mode from being cancelled for invalid friendly targets
    console.log(`🎯 Deploy targeting: Attempting to target friendly unit - server will validate`);
  }

  // ✅ CRITICAL FIX: Validate instanceId exists before sending action
  // This prevents errors when frontend has stale state
  if (!selectedDeployInstanceId) {
    console.error('❌ targetDeploy: selectedDeployInstanceId is missing!', {
      selectedDeployOrderId,
      selectedDeployZone,
      targetUnitId,
      targetZone
    });
    setError("Internal error: Unit instance not found. Please refresh the page.");
    // Clear targeting mode to prevent stuck state
    setDeployTargetMode(false);
    setSelectedDeployOrderId(null);
    setSelectedDeployInstanceId(null);
    setSelectedDeployZone(null);
    setDeployTargetInfo(null);
    return;
  }

  // ✅ CRITICAL FIX: Validate instanceId exists in game state if available
  // This prevents sending stale instanceIds that don't exist in the backend
  let finalInstanceId = selectedDeployInstanceId; // Use local variable that can be updated
  
  if (gameState && playerId && selectedDeployZone) {
    const player = gameState.state?.[playerId];
    if (player) {
      const zoneUnits = player.playedOrders?.[selectedDeployZone] || [];
      const unitExists = zoneUnits.some(u => u && u.instanceId === selectedDeployInstanceId);
      
      if (!unitExists) {
        // Check if backend has a different instanceId (sync issue)
        const backendInstanceId = player.selectedDeployInstanceId;
        const backendOrderId = player.selectedDeployOrderId;
        const backendZone = player.selectedDeployZone;
        
        // ✅ CRITICAL: Always prefer backend's authoritative instanceId if it exists and is valid
        if (backendInstanceId && backendOrderId === selectedDeployOrderId && backendZone === selectedDeployZone) {
          // Verify backend instanceId exists in zone
          const backendUnitExists = zoneUnits.some(u => u && u.instanceId === backendInstanceId);
          
          if (backendUnitExists) {
            console.warn('⚠️ targetDeploy: Frontend instanceId mismatch with backend, using backend instanceId', {
              frontend: selectedDeployInstanceId,
              backend: backendInstanceId,
              orderId: selectedDeployOrderId,
              zone: selectedDeployZone
            });
            // Use backend's authoritative instanceId
            setSelectedDeployInstanceId(backendInstanceId);
            // Update the instanceId we'll use in the action
            finalInstanceId = backendInstanceId;
          } else {
            // Backend instanceId also doesn't exist - this is a real error
            console.error('❌ targetDeploy: Both frontend and backend instanceIds not found in game state!', {
              frontendInstanceId: selectedDeployInstanceId,
              backendInstanceId: backendInstanceId,
              zone: selectedDeployZone,
              orderId: selectedDeployOrderId,
              availableUnits: zoneUnits.map(u => u ? { id: u.id, name: u.name, instanceId: u.instanceId } : null)
            });
            setError("Unit not found. The game state may have changed. Please refresh the page.");
            // Clear targeting mode to prevent stuck state
            setDeployTargetMode(false);
            setSelectedDeployOrderId(null);
            setSelectedDeployInstanceId(null);
            setSelectedDeployZone(null);
            setDeployTargetInfo(null);
            return;
          }
        } else {
          // Backend doesn't have instanceId or it's for a different unit - this is a real error
          console.error('❌ targetDeploy: Unit with instanceId not found in game state!', {
            instanceId: selectedDeployInstanceId,
            zone: selectedDeployZone,
            orderId: selectedDeployOrderId,
            backendInstanceId: backendInstanceId,
            backendOrderId: backendOrderId,
            backendZone: backendZone,
            availableUnits: zoneUnits.map(u => u ? { id: u.id, name: u.name, instanceId: u.instanceId } : null)
          });
          setError("Unit not found. The game state may have changed. Please refresh the page.");
          // Clear targeting mode to prevent stuck state
          setDeployTargetMode(false);
          setSelectedDeployOrderId(null);
          setSelectedDeployInstanceId(null);
          setSelectedDeployZone(null);
          setDeployTargetInfo(null);
          return;
        }
      }
    }
  }

  // Create target info
  const targetInfo = {
    targetUnitId,
    targetZone,
    targetIndex
  };

  // Send targeting action for the already deployed unit.
  // Include gameId so the server uses the correct game (fixes War Effort / multi-tab wrong-game bug).
  const action = {
    type: "targetDeploy",
    gameId: gameId || undefined,
    orderId: selectedDeployOrderId,
    instanceId: finalInstanceId,
    zone: selectedDeployZone,
    targetUnitId,
    targetZone,
    targetIndex
  };

  // Campaign tutorial: validate deploy target against allowedDeployTargetsByOrder before sending
  if (gameState?.campaignMode && gameState?.scriptState?.currentAllowedActions?.allowedDeployTargetsByOrder && selectedDeployOrderId != null && playerId != null) {
    const byOrder = gameState.scriptState.currentAllowedActions.allowedDeployTargetsByOrder;
    const allowedTargetIds = byOrder[selectedDeployOrderId] ?? byOrder[String(selectedDeployOrderId)];
    if (Array.isArray(allowedTargetIds) && allowedTargetIds.length > 0) {
      const selfId = String(playerId);
      const opponentId = gameState.players?.find(id => String(id) !== selfId);
      const zoneKey = String(targetZone || '').replace(/^opponent_/, '');
      const targetPlayerKey = targetUnitId === 'opponent' ? opponentId : selfId;
      const targetUnit = gameState.state?.[targetPlayerKey]?.playedOrders?.[zoneKey]?.[targetIndex];
      const targetOrderId = targetUnit?.id ?? targetUnit?.orderId;
      if (targetOrderId == null || !allowedTargetIds.map(id => Number(id)).includes(Number(targetOrderId))) {
        setError('This unit can only target the indicated unit in this step');
        return;
      }
    }
  }

  console.log('🎯 FRONTEND targetDeploy: Sending action to backend:', action);
  ws.send(JSON.stringify(action));

  // Optimistically close the dialog; syncDeployTargeting re-opens if backend still needs a target.
  setDeployTargetMode(false);
  setSelectedDeployOrderId(null);
  setSelectedDeployInstanceId(null);
  setSelectedDeployZone(null);
  setDeployTargetInfo(null);
};

export default targetDeploy;
